import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { SxProps } from '@mui/system';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { FC, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';

import { defineStyles, theme } from '../../theme';
import { IpsosLogo } from '../../images';

const styles = defineStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'start',
    columnGap: 1,
    '& *': {
      color: theme.vars.palette.common.white,
    },
  },
  title: {
    fontSize: 16,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: 700,
  },
  copyable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  copiedIndicator: {
    position: 'fixed',
    zIndex: 9999,
    pointerEvents: 'none',
    transform: 'translate(10px, -18px)',
  },
  copiedToast: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.1,
    py: 0.5,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    color: theme.vars.palette.common.white,
    backgroundColor: 'rgba(0,0,0,0.72)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
    backdropFilter: 'blur(6px)',
    '@keyframes copiedToastFade': {
      '0%': { opacity: 0, transform: 'translateY(4px) scale(0.98)' },
      '12%': { opacity: 1, transform: 'translateY(0) scale(1)' },
      '88%': { opacity: 1, transform: 'translateY(0) scale(1)' },
      '100%': { opacity: 0, transform: 'translateY(-2px) scale(0.99)' },
    },
    animation: 'copiedToastFade 1000ms ease-in-out both',
  },
});

export type TopBarProjectLinkProps = {
  qreId: string;
  projectName: string;
  surveyNumber?: string | null;
  isLoading: boolean;
  sx?: SxProps;
};

async function copyToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

function onCopyKeyDown(e: KeyboardEvent, text: string, onCopied: (x: number, y: number) => void) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement | null;
    const rect = target?.getBoundingClientRect();
    const x = rect ? rect.right : window.innerWidth / 2;
    const y = rect ? rect.top : window.innerHeight / 2;
    void copyToClipboard(text).then(() => onCopied(x, y));
  }
}

const TopBarProjectLink: FC<TopBarProjectLinkProps> = ({ qreId, projectName, surveyNumber, isLoading, sx }) => {
  const [copiedAt, setCopiedAt] = useState<{ x: number; y: number; nonce: number } | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  const showCopied = (x: number, y: number) => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    setCopiedAt({ x, y, nonce: Date.now() });
    hideTimerRef.current = window.setTimeout(() => setCopiedAt(null), 1000);
  };

  // Loading stav
  if (isLoading) {
    return (
      <Box sx={{ ...styles.root, ...sx }}>
        <IpsosLogo />
        <Box sx={styles.titleContainer}>
          <Typography sx={styles.title}>Načítání...</Typography>
          <Typography sx={styles.subtitle}>Prosím čekejte</Typography>
        </Box>
      </Box>
    );
  }

  const qreIdText = qreId || 'N/A';
  const surveyNumberText = surveyNumber || '';

  return (
    <Box sx={{ ...styles.root, ...sx }}>
      <MuiLink sx={styles.titleContainer} href={`/?qreId=${qreId}`}>
        <IpsosLogo />
      </MuiLink>
      <Box sx={styles.titleContainer}>
        <Typography sx={styles.title}>{projectName || 'Neznámý projekt'}</Typography>
        <Box>
          <Typography
            sx={[styles.subtitle, styles.copyable]}
            component="span"
            role="button"
            tabIndex={0}
            aria-label="Zkopírovat QRE ID"
            onClick={(e: MouseEvent) => void copyToClipboard(qreIdText).then(() => showCopied(e.clientX, e.clientY))}
            onKeyDown={(e) => onCopyKeyDown(e, qreIdText, showCopied)}
          >
            {qreIdText}
          </Typography>
          <Typography
            sx={surveyNumber ? [styles.subtitle, styles.copyable] : styles.subtitle}
            component="span"
            role={surveyNumber ? 'button' : undefined}
            tabIndex={surveyNumber ? 0 : undefined}
            aria-label={surveyNumber ? 'Zkopírovat číslo survey' : undefined}
            onClick={
              surveyNumber
                ? (e: MouseEvent) => void copyToClipboard(surveyNumberText).then(() => showCopied(e.clientX, e.clientY))
                : undefined
            }
            onKeyDown={surveyNumber ? (e) => onCopyKeyDown(e, surveyNumberText, showCopied) : undefined}
          >
            {surveyNumber ? ` / ${surveyNumber}` : null}
          </Typography>
        </Box>
      </Box>
      {copiedAt ? (
        <Box key={copiedAt.nonce} sx={{ ...styles.copiedIndicator, left: copiedAt.x, top: copiedAt.y }}>
          <Box sx={styles.copiedToast}>
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default TopBarProjectLink;
